import type { VideoSummary } from '../types';
import { applyUplift, formatAvv } from '../format';
import { niceMax } from '../scale';
import { useInView } from '../hooks/useInView';

/**
 * Lightweight overlay bar chart - actual views vs projected-with-Lickd per
 * recent video. Hand-rolled (no Chart.js) so the bundle stays self-contained.
 *
 * Built from divs rather than SVG: the axis labels have to be real text at a
 * true font size, and an SVG viewBox scales its contents with the container,
 * so text inside it would be magnified on desktop and unreadable on mobile.
 * Keeping the bars as divs too puts the whole chart in one coordinate system -
 * bar heights are percentages of the plot, labels are CSS pixels, and nothing
 * has to be kept in sync between the two.
 */
interface Props {
  videos: VideoSummary[];
  medianUplift: number;
}

export function Chart({ videos, medianUplift }: Props) {
  const [ref, inView] = useInView<HTMLDivElement>('-40px');

  const data = [...videos].reverse().slice(-10); // oldest → newest, max 10
  if (!data.length) return null;

  const projected = data.map((v) => applyUplift(v.views, medianUplift));
  const rawMax = Math.max(...projected, ...data.map((v) => v.views), 1);

  // Bars are sized against the rounded axis maximum, not the raw data maximum,
  // or the tallest bar would overshoot the top label.
  const axisMax = niceMax(rawMax);
  const height = (val: number) => `${Math.max(Math.round((val / axisMax) * 100), 0)}%`;

  return (
    <div className="chart-plot">
      {/* aria-hidden: a bare pair of numbers would be noise, and the table
          below states the same figures in context. */}
      <div className="chart-axis" aria-hidden="true">
        <div className="chart-axis-tick chart-axis-tick-max">{formatAvv(axisMax, false)}</div>
        <div className="chart-axis-tick chart-axis-tick-zero">0</div>
      </div>
      {/* The bars are decorative: role="img" would collapse them to a single
          generic label and the per-bar titles would never be reached. The
          table below carries the same data for assistive tech. */}
      <div className="chart-grid-wrapper" aria-hidden="true">
        <div ref={ref} className={`chart-grid${inView ? ' in-view' : ''}`}>
          {data.map((v, i) => (
            <div className="chart-slot" key={i}>
              <div
                className="chart-bar chart-bar-actual"
                style={{ height: height(v.views), animationDelay: `${i * 60}ms` }}
                title={`Actual: ${formatAvv(v.views)} views`}
              />
              <div
                className="chart-bar chart-bar-lickd"
                style={{ height: height(projected[i]!), animationDelay: `${i * 60 + 30}ms` }}
                title={`With Lickd: ${formatAvv(projected[i]!)} views`}
              />
            </div>
          ))}
        </div>
      </div>

      <table className="visually-hidden">
        <caption>
          Your last 10 videos: actual views versus projected views with Lickd, oldest first.
        </caption>
        <thead>
          <tr>
            <th scope="col">Video</th>
            <th scope="col">Actual views</th>
            <th scope="col">Views with Lickd</th>
          </tr>
        </thead>
        <tbody>
          {data.map((v, i) => (
            <tr key={i}>
              <th scope="row">{v.title}</th>
              <td>{formatAvv(v.views)}</td>
              <td>{formatAvv(projected[i]!)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
